import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import {
  createTransactionFieldsSelectOptions,
  generateDisplayErrorMessage,
  generateTierTypes,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";

import * as Sentry from "@sentry/browser";
import { Spin, message } from "antd";
import { AxiosResponse } from "axios";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import { loanSecurityTypes } from "../helpers/constants";

import { FormInstance } from "antd/lib/form";
import {
  APIError,
  FinancialInstitution,
  FormFieldConfig,
  SelectOptionsInterface,
  TransactionFields,
  TransactionFieldsEnum,
} from "../helpers/shared-interfaces";
import { GET_FINANCIAL_INSTITUTIONS } from "../gql/general";
import { CREATE_PRODUCT, GET_PRODUCT_CATEGORIES } from "../gql/products";
import { GET_OPPORTUNITY_PROSPECTS } from "../gql/opportunity";

interface State {
  name: string;
  isLoading: boolean;
  opportunityProspectId: string;
  productCategoryId: string;
  financialInstitutions: FinancialInstitution[];
  prospects: SelectOptionsInterface[];
  categories: SelectOptionsInterface[];
  transactionFields: TransactionFields;
  mainProductId?: string
  isEzHomeProduct: boolean
  ezHomeFieldsToHide: string[]
  isLoanOrFixedIncome: boolean;
  isLoanProduct: boolean;
}

const ezHomeFields = [
  "developerName",
  "developerContact",
  "costOfProperty",
  "productFundingOption",
  "mortgageDetails",
  "specificationsOfProperty",
  "accruedBenefitDistribution",
  "accruedBenefitBalance",
];

const loanAndFixedIncomeFields = [
  "interestPerAnnum",
  "commissionFee",
  "processingFee",
  "insuranceFee",
  "maxAmount",
  "maxTenor",
  "minAmount",
  "maxBalancePercentageAccessible",
  "lienAmountMultiplier",
  "loanAndFixedIncomeSpacer",
];

const loanOnlyFields = ["loanSecurityType"];

interface RouteParams {
  id: string;
}

export default class CreateProduct extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      opportunityProspectId: "",
      productCategoryId: "",
      isLoading: true,
      prospects: [],
      categories: [],
      financialInstitutions: [],
      transactionFields: {},
      isEzHomeProduct: false,
      ezHomeFieldsToHide: ezHomeFields,
      isLoanOrFixedIncome: false,
      isLoanProduct: false,
    };
    this.reference = React.createRef<FormInstance>();

    this.redirectToPage = this.redirectToPage.bind(this);
    this.getProspectsAndCategories = this.getProspectsAndCategories.bind(this);

    this.createProduct = this.createProduct.bind(this);
    this.handleProductCategoryChange =
      this.handleProductCategoryChange.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  changeIsEzHomeProduct = (checkedValue: boolean) => {
    this.setState({
      ezHomeFieldsToHide: checkedValue ? [] : ezHomeFields,
      isEzHomeProduct: checkedValue,
    });
  };

  isLoanOrFixedIncomeProvider = (providerName?: string): boolean => {
    const name = providerName?.toLowerCase();
    return name === "loans" || name === "fixed income";
  };

  isLoanProvider = (providerName?: string): boolean => {
    const name = providerName?.toLowerCase();
    return name === "loans";
  };

  handleProductCategoryChange(value: string): void {
    const selectedCategory = this.state.categories.find(
      (category) => category.id === value,
    );
    const categoryName = selectedCategory?.name;

    this.setState({
      isLoanOrFixedIncome: this.isLoanOrFixedIncomeProvider(categoryName),
      isLoanProduct: this.isLoanProvider(categoryName),
    });
  }

  handleTransactionsFieldSelect = async (value: string): Promise<void> => {
    const values = value as unknown as string[];

    const transactionsHash: TransactionFields = values.reduce<TransactionFields>((acc, key) => {
      const label = (TransactionFieldsEnum as Record<string, string>)[key];
      if (label) acc[key] = label;
      return acc;
    }, {});

    this.setState({ transactionFields: transactionsHash });
  };

  getFinancialInstitutionals = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
        isLoading: false,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  async createProduct(): Promise<void> {
    const { mainProductId } = this.state
    interface Response {
      data?: {
        createProduct: {
          product: SelectOptionsInterface;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;
    const { transactionFields, isLoanOrFixedIncome, isLoanProduct } = this.state;
    const values = this.reference.current.getFieldsValue();
    const loanAndFixedIncomePayload = {
      interest_per_annum: values.interestPerAnnum,
      commission_fee: values.commissionFee,
      processing_fee: values.processingFee,
      insurance_fee: values.insuranceFee,
      max_amount: values.maxAmount,
      max_tenor: values.maxTenor,
      min_amount: values.minAmount,
      max_balance_percentage_accessible: values.maxBalancePercentageAccessible,
      lien_amount_multiplier: values.lienAmountMultiplier,
      ...(isLoanProduct
        ? { loan_security_type: values.loanSecurityType }
        : {}),
    };

    try {
      const fieldsToValidate = [
        "name",
        "opportunityProspectId",
        "productCategoryId",
        "productTierId",
        "fundCode",
        "financialInstitutionId",
        "vanCode",
        "taxWaiverRequired",
        "developerName",
        "developerContact",
        "costOfProperty",
        "productFundingOption",
        "mortgageDetails",
        "specificationsOfProperty",
        "accruedBenefitDistribution",
        "accruedBenefitBalance"
      ];

      if (isLoanProduct) {
        fieldsToValidate.push("loanSecurityType");
      }

      data = await this.reference.current.validateFields(fieldsToValidate);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_PRODUCT,
        variables: {
          ...data,
          transactionFields: isLoanOrFixedIncome
            ? loanAndFixedIncomePayload
            : transactionFields,
          taxWaiverRequired: data.taxWaiverRequired === "yes" ? true : false,
          mainProductId
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "createProduct",
      );

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Product created sucessfully");
        mainProductId ? this.redirectToPage(`/products/${mainProductId}`) : this.redirectToPage('/products');
      }
    } catch (error) {
      message.error(`Failed to create product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  redirectToPage(path: string): void {
    this.props.history.push(path);
  }

  getAllProspects = (): Promise<AxiosResponse> => {
    return request({
      query: GET_OPPORTUNITY_PROSPECTS,
    });
  };

  getAllCategories = async (): Promise<AxiosResponse> => {
    return request({
      query: GET_PRODUCT_CATEGORIES,
      variables: {
        page: 1,
      },
    });
  };

  async getProspectsAndCategories(): Promise<void> {
    this.setState({ isLoading: true });
    try {
      const [prospects, categories]: any = await Promise.all([
        this.getAllProspects(),
        this.getAllCategories(),
      ]);

      this.setState({
        isLoading: false,
        prospects: prospects.data.data.opportunityProspects,
        categories: categories.data.data.productCategories.results,
      });
    } catch (e) { }
  }

  componentDidMount(): void {
    this.getProspectsAndCategories();
    this.getFinancialInstitutionals();
    this.setState({ mainProductId: this.props.match.params.id })
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      prospects,
      categories,
      financialInstitutions,
      isEzHomeProduct,
      ezHomeFieldsToHide,
      isLoanOrFixedIncome,
      isLoanProduct,
    } =
      this.state;
    const institutions = financialInstitutions.map((institution) => {
      return { id: institution.id, name: institution.name };
    });

    const fields: FormFieldConfig[] = [
      {
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        placeholder: "Name",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "opportunityProspectId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Prospect",
        placeholder: "Prospect",
        selectOptions: prospects,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "productCategoryId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Category",
        placeholder: "Category",
        selectOptions: categories,
        changeHandler: this.handleProductCategoryChange,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "fundCode",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Fund Code",
        placeholder: "Fund Code",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "productTierId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Tier",
        placeholder: "Tier",
        selectOptions: generateTierTypes(),
        colConfig: {
          span: 7,
        },
      },
      {
        name: "taxWaiverRequired",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Is Tax Waiver Required?",
        placeholder: "Is Tax Waiver Required?",
        selectOptions: [
          { name: "Yes", id: "yes" },
          { name: "No", id: "no" },
        ],
        colConfig: {
          span: 7,
        },
      },
      {
        id: "financialInstitutionId",
        name: "financialInstitutionId",
        placeholder: "Financial Institution",
        inputType: "select",
        selectOptions: institutions,
        rule: "string",
        label: "Institution Name",
        required: false,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "vanCode",
        rule: "string",
        inputType: "string",
        required: false,
        label: "VAN Code",
        placeholder: "VAN Code",
        colConfig: {
          span: 7,
        },
      },
      {
        isArrayField: true,
        id: "transactionFields",
        name: "transactionFields",
        inputType: "multipleSelect",
        label: "Transaction Fields",
        rule: "string",
        placeholder: "Select Transaction Fields",
        required: false,
        changeHandler: this.handleTransactionsFieldSelect,
        colConfig: {
          span: 7,
        },
        selectOptions: createTransactionFieldsSelectOptions(),
        elementStyle: {
          marginBottom: "20px",
        },
      },
      {
        name: "loanSecurityType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Loan Security Type",
        placeholder: "Loan Security Type",
        selectOptions: loanSecurityTypes,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "interestPerAnnum",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Interest Per Annum %",
        placeholder: "Enter interest rate",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "commissionFee",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Commission Fee",
        placeholder: "Enter commission fee",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "processingFee",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Processing Fee",
        placeholder: "Enter processing fee",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "insuranceFee",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Insurance Fee",
        placeholder: "Enter insurance fee",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "maxAmount",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Maximum Amount",
        placeholder: "Enter amount",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "minAmount",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Minimum Amount",
        placeholder: "Enter minimum amount",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "maxTenor",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Maximum Tenor (months)",
        placeholder: "Enter maximum tenor",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "maxBalancePercentageAccessible",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Max Balance % Accessible",
        placeholder: "Enter max balance percentage",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "lienAmountMultiplier",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Lien Amount Multiplier",
        placeholder: "Enter lien amount multiplier",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        id: "isEzHomeProduct",
        name: "isEzHomeProduct",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Is EzHome Product?",
        checkboxCheckedValue: isEzHomeProduct ? true : false,
        checkboxChangeHandler: (e) => this.changeIsEzHomeProduct(e.target.checked),
        colConfig: {
          span: 7,
        },
      },
      {
        name: "developerName",
        id: "developerName",
        rule: "string",
        inputType: "string",
        required: false,
        label: "Developer Name",
        placeholder: "Developer Name",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "developerContact",
        id: "developerContact",
        rule: "string",
        inputType: "string",
        required: false,
        label: "Developer Contact",
        placeholder: "Developer Contact",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "productFundingOption",
        id: "productFundingOption",
        rule: "string",
        inputType: "string",
        required: false,
        label: "Product Funding Option",
        placeholder: "Product Funding Option",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "costOfProperty",
        id: "costOfProperty",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Cost of Property",
        placeholder: "Cost of Property",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        name: "accruedBenefitBalance",
        id: "accruedBenefitBalance",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Accrued Benefit Balance",
        placeholder: "Accrued Benefit Balance",
        colConfig: {
          span: 7,
        },
        elementStyle: {
          width: "100%",
        },
      },
      {
        id: "accruedBenefitDistribution",
        name: "accruedBenefitDistribution",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Accrued Benefit Distribution",
        placeholder: "Cash balance, Property Value...",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "specificationsOfProperty",
        name: "specificationsOfProperty",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Specifications of Property",
        placeholder: "Location, size, type...",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "mortgageDetails",
        name: "mortgageDetails",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Mortgage Details",
        placeholder: "Mortgage Details",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "createProduct",
        name: "createProductButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.createProduct,
      },
    ];

    return (
      <Card header="New Product">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              gutter={[16, 16]}
              reference={this.reference}
              fieldsToHide={[
                ...ezHomeFieldsToHide,
                ...(isLoanOrFixedIncome
                  ? [
                      "transactionFields",
                      ...(isLoanProduct ? [] : loanOnlyFields),
                    ]
                  : [...loanAndFixedIncomeFields, ...loanOnlyFields]),
              ]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
